import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkPickupFulfilledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to mark a vehicle pickup as fulfilled
 */
export const markPickupFulfilled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkPickupFulfilledResponse> => {
	const response = await apiClient.post<MarkPickupFulfilledResponse>(
		ordersEndpoints.markPickupFulfilled(servicesProviderId, orderId),
		{},
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) pickup as fulfilled
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkPickupFulfilled = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark pickup as fulfilled without an order ID or services provider ID.',
				);
			}

			return markPickupFulfilled(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_PICKUP_FULFILLED);
		},
	});
};
